type ModalEntry = {
  panel: HTMLElement;
  backdrop: HTMLElement;
  trigger: HTMLElement | null;
  close: () => void;
  depth: number;
};

const stack: ModalEntry[] = [];
const originalInert = new Map<HTMLElement, boolean>();
let observer: MutationObserver | undefined;

function focusables(panel: HTMLElement) {
  return Array.from(panel.querySelectorAll<HTMLElement>(
    'a[href],button,input,select,textarea,[tabindex],[contenteditable="true"]',
  )).filter((element) => element.tabIndex >= 0 && !element.matches(":disabled") && !element.closest("[inert]") && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
}

function syncInert() {
  const top = stack.at(-1);
  for (const [element, inert] of originalInert) element.inert = inert;
  if (!top) {
    originalInert.clear();
    return;
  }
  for (const element of document.body.children) {
    if (!(element instanceof HTMLElement)) continue;
    if (!originalInert.has(element)) originalInert.set(element, element.inert);
    element.inert = element !== top.backdrop && !element.contains(top.backdrop);
  }
}

function onKey(event: KeyboardEvent) {
  const top = stack.at(-1);
  if (!top) return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.isComposing && event.keyCode !== 229) top.close();
  } else if (event.key === "Tab") {
    const elements = focusables(top.panel);
    const currentIndex = elements.indexOf(document.activeElement as HTMLElement);
    event.preventDefault();
    event.stopImmediatePropagation();
    const next = elements.length ? elements[(currentIndex + (event.shiftKey ? -1 : 1) + elements.length) % elements.length] : top.panel;
    (currentIndex < 0 && event.shiftKey ? elements.at(-1) ?? top.panel : next)?.focus();
  }
}

function onFocus(event: FocusEvent) {
  const top = stack.at(-1);
  if (top && event.target instanceof Node && !top.panel.contains(event.target)) {
    (focusables(top.panel)[0] ?? top.panel).focus();
  }
}

export function focusModalIfTop(panel: HTMLElement) {
  if (stack.at(-1)?.panel !== panel) return false;
  panel.focus();
  return true;
}

export function containModalFocus(panel: HTMLElement, initial: HTMLElement | null, close: () => void, depth = 0) {
  const backdrop = panel.parentElement;
  if (!backdrop) return () => {};
  const entry = { panel, backdrop, close, depth, trigger: document.activeElement instanceof HTMLElement ? document.activeElement : null };
  stack.push(entry);
  stack.sort((left, right) => left.depth - right.depth);
  if (stack.length === 1) {
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocus, true);
    observer = new MutationObserver(syncInert);
    observer.observe(document.body, { childList: true });
  }
  syncInert();
  if (stack.at(-1) === entry) (initial && !initial.matches(":disabled") ? initial : panel).focus();
  return () => {
    const wasTop = stack.at(-1) === entry;
    const index = stack.indexOf(entry);
    if (index !== -1) stack.splice(index, 1);
    if (stack.length === 0) {
      observer?.disconnect();
      observer = undefined;
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocus, true);
    }
    syncInert();
    if (!wasTop) return;
    const top = stack.at(-1);
    if (entry.trigger?.isConnected && !entry.trigger.closest("[inert]") && (!top || top.panel.contains(entry.trigger))) {
      entry.trigger.focus();
    } else if (top) {
      (focusables(top.panel)[0] ?? top.panel).focus();
    }
  };
}
